import { Dialog } from 'primereact/dialog';
import { Dispatch, FC, SetStateAction, useEffect, useRef, useState, MutableRefObject } from 'react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';
import { useFormik } from 'formik';
import { classNames } from 'primereact/utils';
import { Tooltip } from 'primereact/tooltip';
import { Info, SquarePen, X } from 'lucide-react';
import { Class } from '../../../models/Class';
import { Toast } from 'primereact/toast';
import { useLazyLoadQuery, useMutation } from 'react-relay';
import { ClassesAllSitesQuery } from '../../../graphQLOperations/__generated__/ClassesAllSitesQuery.graphql';
import { allGrades, allSites, createClass, updateClass } from '../../../graphQLOperations/Classes';
import { ClassesAllGradesQuery } from '../../../graphQLOperations/__generated__/ClassesAllGradesQuery.graphql';
import { Site } from '../../../models/Site';
import { Grade } from '../../../models/Grade';
import { ClassesCreateClassMutation } from '../../../graphQLOperations/__generated__/ClassesCreateClassMutation.graphql';
import { ClassesUpdateClassMutation } from '../../../graphQLOperations/__generated__/ClassesUpdateClassMutation.graphql';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { selectUser } from '../../../redux/reducers/userSlice';
import { noteClassesChanged } from '../../../redux/reducers/globalClassFilterSlice';
import { encode } from 'entities';

import ClassIconPicker from '../../ClassIcon/ClassIconPicker';
import { useSaveClassIcon } from '../../../custom-hooks/useSaveClassIcon';
import { ClassEditEntryPoint, ClassField, useClassUpdatedEvent } from '../../../custom-hooks/useClassUpdatedEvent';
import { useSideNavActive } from '../../LayoutWrapper/SideNavLayoutContext';
import { useClassIcons } from '../../../custom-hooks/useClassIcons';
import { resolveClassIcon } from '../../../businessLogicService/resolveClassIcon';
import { CLASS_ICON_NAMES } from '../../ClassIcon/classIcons';

interface Props {
  visible: boolean;
  setVisible: Dispatch<
    SetStateAction<{
      display: boolean;
      isEdit: boolean;
    }>
  >;
  isEdit: boolean;
  classList: Class[] | null;
  setClassList: (c: Class[]) => void;
  initialEditValues: Class | null;
  toast: MutableRefObject<Toast | null>;
  setSelectedClass: (c: Class) => void;
  entryPoint?: ClassEditEntryPoint;
}

interface FormValues {
  name: string;
  section: string;
  grade: Array<Grade>;
  site: Site;
}

interface FormValuesError {
  name: string;
  section: string;
  grade: string;
  site: string;
}

const ClassesRosterClassFormDialog: FC<Props> = ({
  isEdit,
  visible,
  setVisible,
  setClassList,
  classList,
  initialEditValues,
  toast,
  setSelectedClass,
  entryPoint = 'class_card_menu',
}) => {
  const userRedux = useAppSelector(selectUser);
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const sideNavActive = useSideNavActive();
  const storedIcons = useClassIcons();
  const [icon, setIcon] = useState<string | null>(null);
  const openedWithIcon = useRef<string | null>(null);
  const saveClassIcon = useSaveClassIcon();
  const reportClassUpdated = useClassUpdatedEvent();

  useEffect(() => {
    if (!visible) return;
    const current =
      isEdit && initialEditValues?.id ? resolveClassIcon(initialEditValues.id, storedIcons, CLASS_ICON_NAMES) : null;
    openedWithIcon.current = current;
    setIcon(current);
  }, [visible, isEdit, initialEditValues?.id, storedIcons]);
  const [sites, setSites] = useState<Array<Site>>([]);
  const [grades, setGrades] = useState<Array<Grade>>([]);
  const sitesData = useLazyLoadQuery<ClassesAllSitesQuery>(allSites, {
    input: { userId: 'UserNode:' + userRedux?.id },
  });
  const gradesLevelsData = useLazyLoadQuery<ClassesAllGradesQuery>(allGrades, {});

  const [createClassMutation] = useMutation<ClassesCreateClassMutation>(createClass);
  const [updateClassMutation] = useMutation<ClassesUpdateClassMutation>(updateClass);

  const sitesTemplate = sitesData.sites?.nodes?.map(element => {
    return element;
  });

  const gradesLevelTemplate = gradesLevelsData.allGradeLevels.map(element => {
    return element;
  });

  const close = () => {
    formik.resetForm();
    setVisible(prev => ({ ...prev, display: false }));
  };
  const onError = (message?: string) => {
    close();
    setLoading(false);
    toast?.current?.show({
      severity: 'error',
      summary: 'Error',
      detail: message ?? 'Something went wrong. Please try again',
    });
  };

  const saveIcon = (classId?: string | null) => saveClassIcon(classId, icon);

  const changedFields = ({ site, name, section, grade }: FormValues): ClassField[] => {
    const gradeIds = (levels?: Array<{ id?: string | null } | null> | null) =>
      (levels ?? [])
        .map(level => level?.id)
        .sort()
        .join(',');

    return (
      [
        name !== initialEditValues?.name && 'name',
        (section ?? '') !== (initialEditValues?.section ?? '') && 'section',
        gradeIds(grade) !== gradeIds(initialEditValues?.gradeLevels) && 'grade',
        site?.id !== initialEditValues?.site?.id && 'site',
        icon !== openedWithIcon.current && 'icon',
      ] as Array<ClassField | false>
    ).filter((field): field is ClassField => !!field);
  };

  const onSubmit = ({ site, name, section, grade }: FormValues) => {
    if (!loading) {
      if (!isEdit && userRedux) {
        setLoading(true);
        const input = {
          siteId: site.id,
          ownerId: `UserNode:${userRedux.id!}`,
          name,
          section,
          isJoinCodeActive: true,
          isActive: true,
          gradeIds: grade?.map(grade => grade.id),
        };
        createClassMutation({
          variables: {
            input,
          },

          onCompleted: data => {
            if (data?.createClass?.classNode !== null) {
              const response = {
                ...(data?.createClass?.classNode as Class),
                grades: (data?.createClass?.classNode as Class)?.gradeLevels?.map(grade => grade?.name)?.join(', '),
                users: [],
              };
              const newClass = {
                ...response,
                sectionName: response?.section ? `${response.name} - ${response.section}` : response?.name,
              };
              saveIcon(newClass.id);
              setSelectedClass(newClass);
              setClassList([...(classList as Class[]), newClass]);
              dispatch(noteClassesChanged());
              show();
              close();
              setLoading(false);
              return;
            }
            onError(data?.createClass?.errors?.[0]?.message);
          },
          onError: () => onError(),
        });
        return;
      }
      if (initialEditValues && initialEditValues?.id && userRedux && isEdit) {
        setLoading(true);
        updateClassMutation({
          variables: {
            input: {
              id: initialEditValues.id,
              siteId: site.id,
              name,
              section,
              isJoinCodeActive: true,
              isActive: true,
              gradeIds: grade?.map(grade => grade.id),
            },
          },
          onCompleted: data => {
            if (data?.updateClass?.classNode !== null) {
              const response = {
                ...data?.updateClass?.classNode,
                grades: data?.updateClass?.classNode?.gradeLevels?.map(grade => grade?.name)?.join(', '),
              };
              const newClass = {
                ...response,
                sectionName: response?.section ? `${response.name} - ${response.section}` : response?.name,
              } as Class;
              saveIcon(newClass.id);
              reportClassUpdated({
                classId: newClass.id,
                fieldsChanged: changedFields({ site, name, section, grade }),
                entryPoint,
                icon,
              });
              setSelectedClass(newClass);
              const newClasses =
                classList?.map(cl => {
                  if (response?.id === cl?.id) {
                    return response as Class;
                  }
                  return cl;
                }) ?? [];
              setClassList(
                newClasses.map(element => ({
                  ...element,
                  sectionName: element?.section ? `${element.name} - ${element.section}` : element?.name,
                })),
              );
              dispatch(noteClassesChanged());
              show();
              close();
              setLoading(false);
              return;
            }
            onError(data?.updateClass?.errors?.[0]?.message);
          },
          onError: () => onError(),
        });
      }
    }
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      name: '',
      section: '',
      grade: [],
      site: { id: '', name: '' },
    },
    validate: data => {
      const errors = {} as FormValuesError;
      if (!data?.name?.trim()) {
        errors.name = 'Name is required.';
      }
      if (!data?.grade?.length) {
        errors.grade = 'At least one grade is required';
      }
      if (!data?.site?.name) {
        errors.site = 'Site is required.';
      }
      return errors;
    },
    onSubmit,
  });

  const summaryContent = isEdit ? 'Class Details Saved' : 'Class Created';
  const detailContent = isEdit
    ? `${encode(formik.values.name ?? '')} has been updated`
    : `${encode(formik.values.name ?? '')} has been created`;

  const show = () => toast?.current?.show({ severity: 'success', summary: summaryContent, detail: detailContent });

  const selectedGradeNames = formik.values.grade?.map(grade => grade?.name).join(', ');
  const hasHiddenGrades = (formik.values.grade?.length ?? 0) > 1;

  const isFormFieldInvalid = (value: keyof FormValues) => !!(formik.touched[value] && formik.errors[value]);
  const getFormErrorMessage = (value: keyof FormValues) => {
    return isFormFieldInvalid(value) ? (
      <small id={`${value}-error`} className="p-error">
        {(formik?.errors as FormValuesError)[value]}
      </small>
    ) : null;
  };

  useEffect(() => {
    if (isEdit) {
      formik.setFieldValue('name', initialEditValues?.name);
      formik.setFieldValue('section', initialEditValues?.section);
      formik.setFieldValue('site', initialEditValues?.site);
      formik.setFieldValue('grade', initialEditValues?.gradeLevels);
    }
  }, [isEdit]);

  useEffect(() => {
    setSites(sitesTemplate as Array<Site>);
    setGrades(gradesLevelTemplate as Grade[]);
  }, []);

  const header = (
    <div className="flex align-items-center gap-md">
      <span className="nc_wizard-header-icon">
        <SquarePen size={17} aria-hidden />
      </span>
      <span className="text-xl font-semibold line-height-1">{isEdit ? 'Edit class details' : 'Create a class'}</span>
    </div>
  );

  const footer = (
    <div className="flex justify-content-between align-items-center gap-md w-full">
      <Button label="Cancel" outlined rounded onClick={close} disabled={loading} />
      <Button
        label="Save"
        icon="nc_icon-nc_save icon-sm"
        rounded
        onClick={() => formik.handleSubmit()}
        loading={loading}
      />
    </div>
  );

  return (
    <Dialog
      header={header}
      footer={footer}
      className="nc_class-wizard"
      style={{ width: '32.5rem' }}
      visible={visible}
      onHide={close}
      dismissableMask={false}
      ariaCloseIconLabel="Close"
    >
      <div className="nc_wizard-form flex flex-column gap-md py-2">
        {sideNavActive && <ClassIconPicker value={icon} onChange={setIcon} />}
        <div className="flex flex-column gap-xs">
          <label htmlFor="name" className="font-bold">
            Class name <span className="nc_required-mark">*</span>
          </label>
          <InputText
            id="name"
            name="name"
            data-testid="name"
            value={formik.values.name}
            onChange={e => {
              formik.setFieldValue('name', e.target.value);
            }}
            className={classNames('w-full', { 'p-invalid': isFormFieldInvalid('name') })}
            aria-required="true"
            aria-invalid={formik?.errors.name && formik?.touched.name ? 'true' : undefined}
            aria-describedby={formik?.errors.name && formik?.touched.name ? 'name-error' : undefined}
            autoComplete="off"
          />
          {getFormErrorMessage('name')}
        </div>
        <div className="flex flex-column gap-xs">
          <label htmlFor="section" className="font-bold flex align-items-center gap-xs">
            Section
            <Tooltip target="#classFormSectionInfo" />
            <span
              id="classFormSectionInfo"
              className="nc_wizard-form__info-icon"
              data-pr-tooltip="This field is helpful if there are multiple classes with the same name. You can use a teacher name and/or class period to differentiate between them."
              data-pr-position="top"
            >
              <Info size={16} aria-hidden />
            </span>
          </label>
          <InputText
            id="section"
            name="section"
            data-testid="section"
            value={formik.values.section}
            onChange={e => {
              formik.setFieldValue('section', e.target.value);
            }}
            className="w-full"
            aria-required="true"
            aria-invalid={formik?.errors.section && formik?.touched.section ? 'true' : undefined}
            aria-describedby={formik?.errors.section && formik?.touched.section ? 'section-error' : undefined}
            autoComplete="off"
          />
          {getFormErrorMessage('section')}
        </div>
        <div className="grid">
          <div className="col-12 md:col-6 flex flex-column gap-xs">
            <label htmlFor="ms_grade" className="font-bold">
              Grade <span className="nc_required-mark">*</span>
            </label>
            <MultiSelect
              inputId="ms_grade"
              name="grade"
              data-testid="grade"
              value={formik.values.grade}
              options={grades}
              optionLabel="name"
              dataKey="id"
              display="chip"
              filter={false}
              removeIcon={<X size={14} strokeWidth={2.5} />}
              maxSelectedLabels={1}
              title={selectedGradeNames}
              className={classNames('w-full', {
                'p-invalid': isFormFieldInvalid('grade'),
                'nc_wizard-form__grades--truncated': hasHiddenGrades,
              })}
              onChange={e => {
                formik.setFieldValue('grade', e.value);
              }}
              placeholder="Select grade"
              aria-required="true"
              aria-invalid={formik?.errors.grade && formik?.touched.grade ? 'true' : undefined}
              aria-describedby={formik?.errors.grade && formik?.touched.grade ? 'grade-error' : undefined}
            />
            {getFormErrorMessage('grade')}
          </div>
          <div className="col-12 md:col-6 flex flex-column gap-xs">
            <label htmlFor="dd_site" className="font-bold">
              Site <span className="nc_required-mark">*</span>
            </label>
            <Dropdown
              inputId="dd_site"
              name="site"
              data-testid="site"
              value={formik?.values?.site}
              options={sites}
              optionLabel="name"
              dataKey="id"
              className={classNames('w-full', 'nc_locked-site-select', { 'p-invalid': isFormFieldInvalid('site') })}
              onChange={e => {
                formik.setFieldValue('site', e.value);
              }}
              placeholder="Select site"
              aria-required="true"
              aria-invalid={formik?.errors.site && formik?.touched.site ? 'true' : undefined}
              aria-describedby={formik?.errors.site && formik?.touched.site ? 'site-error' : undefined}
              disabled={isEdit}
            />
            {getFormErrorMessage('site')}
          </div>
        </div>
        <span className="text-color-secondary text-sm">
          <span className="nc_required-mark">*</span> Required fields
        </span>
      </div>
    </Dialog>
  );
};

export default ClassesRosterClassFormDialog;
